import {
  startRegistration,
  startAuthentication,
  browserSupportsWebAuthn,
  platformAuthenticatorIsAvailable,
} from '@simplewebauthn/browser';
import type {
  PublicKeyCredentialCreationOptionsJSON,
  PublicKeyCredentialRequestOptionsJSON,
} from '@simplewebauthn/browser';
import type { AuthStatus, VerifyResult } from '@pipyar/core';
import { api, ApiRequestError } from './api';

/**
 * Passkey ceremonies, browser side.
 *
 * Each is two round trips: ask the server for options, hand them to the browser's
 * WebAuthn API, then post the signed result back for verification.
 */

export const authApi = {
  status: () => api.get<AuthStatus>('/api/auth/status'),
  logout: () => api.post<{ ok: true }>('/api/auth/logout'),
};

export { browserSupportsWebAuthn, platformAuthenticatorIsAvailable };

/**
 * Turn a WebAuthn exception into something worth showing a person.
 *
 * The browser throws DOMExceptions whose messages are written for developers
 * ("The operation either timed out or was not allowed..."), so the common cases
 * get rewritten. Cancelling is not an error worth shouting about — it is the
 * normal outcome of dismissing the system dialog.
 */
function describeWebAuthnError(err: unknown): string {
  if (err instanceof ApiRequestError) return err.message;

  if (err instanceof Error) {
    switch (err.name) {
      case 'NotAllowedError':
        return 'Cancelled, or the request timed out. Try again when you are ready.';
      case 'InvalidStateError':
        return 'This device already has a passkey registered for Pipyar. Try signing in instead.';
      case 'NotSupportedError':
        return 'This device cannot create a passkey of the type the server asked for.';
      case 'SecurityError':
        return 'Passkeys need a secure connection. Make sure you are on https:// and that the address matches the server configuration.';
      case 'AbortError':
        return 'The passkey prompt was dismissed.';
      default:
        return err.message || 'Something went wrong talking to your authenticator.';
    }
  }
  return 'Something went wrong talking to your authenticator.';
}

export class PasskeyError extends Error {
  // Uses the standard ErrorOptions `cause` rather than declaring its own field,
  // which would shadow the one Error already defines.
  constructor(message: string, options?: { cause?: unknown }) {
    super(message, options);
    this.name = 'PasskeyError';
  }
}

/** First-run: pick a username and create the one passkey that owns this install. */
export async function registerPasskey(username: string): Promise<VerifyResult> {
  const options = await api.post<PublicKeyCredentialCreationOptionsJSON>(
    '/api/auth/register/options',
    { username },
  );

  let attestation;
  try {
    attestation = await startRegistration({ optionsJSON: options });
  } catch (err) {
    throw new PasskeyError(describeWebAuthnError(err), { cause: err });
  }

  return await api.post<VerifyResult>('/api/auth/register/verify', attestation);
}

/**
 * Returning visits: sign in with no username.
 *
 * The server sends an empty allowCredentials list, so the browser shows its own
 * chooser for whichever passkeys it holds for this domain.
 */
export async function loginWithPasskey(): Promise<VerifyResult> {
  const options = await api.post<PublicKeyCredentialRequestOptionsJSON>(
    '/api/auth/login/options',
  );

  let assertion;
  try {
    assertion = await startAuthentication({ optionsJSON: options });
  } catch (err) {
    throw new PasskeyError(describeWebAuthnError(err), { cause: err });
  }

  return await api.post<VerifyResult>('/api/auth/login/verify', assertion);
}
